import React, { useState, useEffect } from 'react';
import NavigationHeader from '../components/NavigationHeader';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Video, Monitor, FileText, Smartphone, TrendingUp, 
  Play, Users, CheckCircle2, BarChart3, Brain, ArrowRight,
  Zap, Target, Activity, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

const TechnologyPage = () => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Draft App screenshots
  const appScreenshots = [
    {
      url: 'https://customer-assets.emergentagent.com/job_b570fcaf-b7b3-47f0-95de-02dc6c165051/artifacts/8ygi7xs3_image%20%283%29.png',
      alt: 'Draft App - Dashboard'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_b570fcaf-b7b3-47f0-95de-02dc6c165051/artifacts/bxurb2hw_image%20%284%29.png',
      alt: 'Draft App - Tests'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_b570fcaf-b7b3-47f0-95de-02dc6c165051/artifacts/sqndzfub_image%20%285%29.png',
      alt: 'Draft App - Results'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_b570fcaf-b7b3-47f0-95de-02dc6c165051/artifacts/igwyyugp_image%20%286%29.png',
      alt: 'Draft App - Analysis'
    }
  ];

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % appScreenshots.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [appScreenshots.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % appScreenshots.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + appScreenshots.length) % appScreenshots.length);
  };

  const technologies = [
    { icon: Video, nameKey: 'veoCamera', taglineKey: 'veoCameraTagline', color: "blue" },
    { icon: BarChart3, nameKey: 'videoAnalysis', taglineKey: 'videoAnalysisTagline', color: "purple" },
    { icon: Brain, nameKey: 'draftApp', taglineKey: 'draftAppTagline', color: "green" },
    { icon: FileText, nameKey: 'evaluationReports', taglineKey: 'evaluationReportsTagline', color: "orange" }
  ];

  return (
    <div className="min-h-screen">
      <NavigationHeader />
      
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-blue-500 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500 rounded-full blur-3xl animate-pulse"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-blue-600/20 backdrop-blur-sm border border-blue-500/30 rounded-full px-6 py-2 mb-6">
              <Zap className="h-5 w-5 mr-2" />
              <span className="font-bold text-sm uppercase tracking-wider">{t('technology.badge')}</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
              {t('technology.heroTitle')}
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-200 font-medium max-w-4xl mx-auto mb-12">
              {t('technology.heroSubtitle')}
            </p>
          </div>

          {/* Technology Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {technologies.map((tech, index) => {
              const Icon = tech.icon;
              const colorClasses = {
                blue: "from-blue-600 to-blue-700",
                purple: "from-purple-600 to-purple-700",
                green: "from-green-600 to-green-700",
                orange: "from-orange-600 to-orange-700"
              };
              
              return (
                <Card 
                  key={index}
                  className={`p-6 bg-gradient-to-br ${colorClasses[tech.color]} text-white transform hover:scale-105 transition-all duration-300 hover:shadow-2xl`}
                >
                  <Icon className="h-12 w-12 mb-4" />
                  <h3 className="text-xl font-black mb-2">{t(`technology.${tech.nameKey}`)}</h3>
                  <p className="text-sm opacity-90">{t(`technology.${tech.taglineKey}`)}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Veo Camera Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Video className="h-16 w-16 text-blue-600 mb-6" />
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
                {t('technology.veoCameraSystem')}
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                {t('technology.veoCameraSystemDesc')}
              </p>

              <div className="space-y-4 mb-8">
                {[1, 2, 3, 4, 5].map((num) => (
                  <div key={num} className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{t(`technology.veoFeature${num}`)}</span>
                  </div>
                ))}
              </div>

              <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200">
                <Play className="h-10 w-10 text-blue-600 mb-3" />
                <h3 className="text-xl font-black text-gray-900 mb-2">{t('technology.liveStreamingFamilies')}</h3>
                <p className="text-gray-700">
                  {t('technology.liveStreamingFamiliesDesc')}
                </p>
              </Card>
            </div>

            <div className="relative">
              <Card className="p-8 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
                <div className="aspect-video rounded-lg mb-6 overflow-hidden">
                  <iframe
                    src="https://player.vimeo.com/video/1161860940?badge=0&autopause=0&player_id=0&app_id=58479"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                    className="w-full h-full"
                    title="Veo Camera - Oakham Football Camps"
                  ></iframe>
                </div>
                <h3 className="text-2xl font-black mb-4">{t('technology.professionalRecording')}</h3>
                <p className="text-blue-100 mb-6">
                  {t('technology.professionalRecordingDesc')}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/10 rounded-lg p-4 text-center">
                    <p className="text-3xl font-black">HD</p>
                    <p className="text-sm text-blue-200">{t('technology.quality')}</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 text-center">
                    <p className="text-3xl font-black">Live</p>
                    <p className="text-sm text-blue-200">{t('technology.streaming')}</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 text-center">
                    <p className="text-3xl font-black">Cloud</p>
                    <p className="text-sm text-blue-200">{t('technology.storage')}</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 text-center">
                    <p className="text-3xl font-black">24/7</p>
                    <p className="text-sm text-blue-200">{t('technology.access')}</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Video Analysis Section */}
      <section className="py-20 bg-gradient-to-br from-purple-900 to-purple-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <Card className="p-8 bg-white/10 backdrop-blur-lg border-white/20">
                <BarChart3 className="h-16 w-16 mb-6" />
                <h3 className="text-3xl font-black mb-6">{t('technology.interactiveAnalysis')}</h3>
                <div className="space-y-4">
                  <div className="bg-white/10 rounded-lg p-4">
                    <h4 className="font-black mb-2">{t('technology.preCampEvaluation')}</h4>
                    <p className="text-purple-100 text-sm">{t('technology.preCampEvaluationDesc')}</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4">
                    <h4 className="font-black mb-2">{t('technology.duringCampSessions')}</h4>
                    <p className="text-purple-100 text-sm">{t('technology.duringCampSessionsDesc')}</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4">
                    <h4 className="font-black mb-2">{t('technology.activeParticipation')}</h4>
                    <p className="text-purple-100 text-sm">{t('technology.activeParticipationDesc')}</p>
                  </div>
                </div>
              </Card>
            </div>

            <div className="order-1 lg:order-2">
              <Users className="h-16 w-16 mb-6" />
              <h2 className="text-4xl md:text-5xl font-black mb-6">
                {t('technology.professionalVideoAnalysis')}
              </h2>
              <p className="text-xl text-purple-100 mb-8">
                {t('technology.professionalVideoAnalysisDesc')}
              </p>

              <div className="space-y-4 mb-8">
                {[1, 2, 3, 4, 5, 6].map((num) => (
                  <div key={num} className="flex items-start">
                    <CheckCircle2 className="h-6 w-6 text-purple-300 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-purple-100">{t(`technology.analysisFeature${num}`)}</span>
                  </div>
                ))}
              </div>

              <Card className="p-6 bg-white/10 backdrop-blur-sm border-white/20">
                <Target className="h-10 w-10 mb-3" />
                <h3 className="text-xl font-black mb-2">{t('technology.personalizedFocus')}</h3>
                <p className="text-purple-100">
                  {t('technology.personalizedFocusDesc')}
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Evaluation Reports Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <FileText className="h-16 w-16 text-orange-600 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              {t('technology.comprehensiveReports')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('technology.comprehensiveReportsDesc')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Card className="p-8 bg-white">
              <h3 className="text-2xl font-black text-gray-900 mb-6">{t('technology.reportIncludes')}</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-orange-600 font-black">1</span>
                  </div>
                  <div>
                    <h4 className="font-black text-gray-900 mb-1">{t('technology.technicalSkills')}</h4>
                    <p className="text-sm text-gray-600">{t('technology.technicalSkillsDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-orange-600 font-black">2</span>
                  </div>
                  <div>
                    <h4 className="font-black text-gray-900 mb-1">{t('technology.tacticalUnderstanding')}</h4>
                    <p className="text-sm text-gray-600">{t('technology.tacticalUnderstandingDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-orange-600 font-black">3</span>
                  </div>
                  <div>
                    <h4 className="font-black text-gray-900 mb-1">{t('technology.physicalPerformance')}</h4>
                    <p className="text-sm text-gray-600">{t('technology.physicalPerformanceDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-orange-600 font-black">4</span>
                  </div>
                  <div>
                    <h4 className="font-black text-gray-900 mb-1">{t('technology.mentalSocialSkills')}</h4>
                    <p className="text-sm text-gray-600">{t('technology.mentalSocialSkillsDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-orange-600 font-black">5</span>
                  </div>
                  <div>
                    <h4 className="font-black text-gray-900 mb-1">{t('technology.recommendations')}</h4>
                    <p className="text-sm text-gray-600">{t('technology.recommendationsDesc')}</p>
                  </div>
                </div>
              </div>
            </Card>

            <div className="bg-gradient-to-br from-orange-50 to-white border-4 border-orange-200 rounded-2xl p-8">
              <div className="aspect-[3/4] bg-white rounded-lg shadow-2xl overflow-hidden border-2 border-gray-200 relative group">
                <iframe
                  src="https://customer-assets.emergentagent.com/job_b570fcaf-b7b3-47f0-95de-02dc6c165051/artifacts/t0ev7b9b_Example-Report.pdf.pdf#toolbar=0&navpanes=0"
                  className="w-full h-full"
                  title="Sample Evaluation Report"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-6">
                  <a 
                    href="https://customer-assets.emergentagent.com/job_b570fcaf-b7b3-47f0-95de-02dc6c165051/artifacts/t0ev7b9b_Example-Report.pdf.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-orange-600 hover:bg-orange-500 text-white font-bold px-6 py-3 rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <FileText className="h-5 w-5" />
                    {t('technology.viewFullReport') || 'View Full Report'}
                  </a>
                </div>
              </div>
              <div className="text-center mt-4">
                <a 
                  href="https://customer-assets.emergentagent.com/job_b570fcaf-b7b3-47f0-95de-02dc6c165051/artifacts/t0ev7b9b_Example-Report.pdf.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 font-semibold"
                >
                  <FileText className="h-4 w-4" />
                  {t('technology.downloadSampleReport') || 'Download Sample Report (PDF)'}
                </a>
                <p className="text-sm text-gray-600 mt-2">
                  {t('technology.reportDelivered')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Draft App Section */}
      <section className="py-20 bg-gradient-to-br from-green-600 to-green-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Brain className="h-16 w-16 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              {t('technology.aiPoweredAssessment')}
            </h2>
            <p className="text-xl text-green-100 max-w-3xl mx-auto">
              {t('technology.aiPoweredAssessmentDesc')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start mb-12">
            {/* Left Column - How It Works + Track Progress */}
            <div className="space-y-6">
              <Card className="p-8 bg-white/10 backdrop-blur-lg border-white/20">
                <Smartphone className="h-12 w-12 mb-6" />
                <h3 className="text-3xl font-black mb-6">{t('technology.howItWorks')}</h3>
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                        <span className="font-black text-sm">1</span>
                      </div>
                      <h4 className="font-black">{t('technology.downloadApp')}</h4>
                    </div>
                    <p className="text-green-100 text-sm ml-11">{t('technology.downloadAppDesc')}</p>
                  </div>
                  <div>
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                        <span className="font-black text-sm">2</span>
                      </div>
                      <h4 className="font-black">{t('technology.setupTestScene')}</h4>
                    </div>
                    <p className="text-green-100 text-sm ml-11">{t('technology.setupTestSceneDesc')}</p>
                  </div>
                  <div>
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                        <span className="font-black text-sm">3</span>
                      </div>
                      <h4 className="font-black">{t('technology.completeTests')}</h4>
                    </div>
                    <p className="text-green-100 text-sm ml-11">{t('technology.completeTestsDesc')}</p>
                  </div>
                  <div>
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                        <span className="font-black text-sm">4</span>
                      </div>
                      <h4 className="font-black">{t('technology.getResults')}</h4>
                    </div>
                    <p className="text-green-100 text-sm ml-11">{t('technology.getResultsDesc')}</p>
                  </div>
                </div>
              </Card>

              {/* Track Progress - Now under How It Works */}
              <Card className="p-6 bg-white/10 backdrop-blur-sm border-white/20">
                <div className="flex items-start gap-4">
                  <TrendingUp className="h-10 w-10 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-black mb-2">{t('technology.trackProgress')}</h3>
                    <p className="text-green-100">
                      {t('technology.trackProgressDesc')}
                    </p>
                  </div>
                </div>
              </Card>

              {/* Objective Assessment - Below Track Progress */}
              <Card className="p-6 bg-white/10 backdrop-blur-sm border-white/20">
                <p className="text-lg font-bold mb-2">{t('technology.objectiveAssessment')}</p>
                <p className="text-green-100">
                  {t('technology.objectiveAssessmentDesc')}
                </p>
              </Card>
            </div>

            {/* Right Column - What We Measure + App Preview */}
            <div className="space-y-6">
              <Card className="p-8 bg-white/10 backdrop-blur-lg border-white/20">
                <Activity className="h-12 w-12 mb-6" />
                <h3 className="text-3xl font-black mb-6">{t('technology.whatWeMeasure')}</h3>
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5, 6].map((num) => (
                    <div key={num} className="flex items-start">
                      <div className="w-2 h-2 bg-green-300 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                      <span className="text-green-100">{t(`technology.draftFeature${num}`)}</span>
                    </div>
                  ))}
                </div>
              </Card>

              {/* App Screenshots Carousel */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h4 className="text-xl font-black mb-4 text-center">{t('technology.appPreview') || 'App Preview'}</h4>
                <div className="relative">
                  {/* Main Image */}
                  <div className="relative aspect-[9/16] max-w-[220px] mx-auto bg-black rounded-3xl overflow-hidden shadow-2xl border-4 border-gray-800">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <img 
                        src={appScreenshots[currentSlide].url}
                        alt={appScreenshots[currentSlide].alt}
                        className="w-full h-full object-cover transition-opacity duration-500"
                      />
                    </div>
                    {/* Phone notch */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-b-xl"></div>
                  </div>

                  {/* Navigation Arrows */}
                  <button 
                    onClick={prevSlide}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                    aria-label="Previous slide"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button 
                    onClick={nextSlide}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                    aria-label="Next slide"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>

                {/* Dots Indicator */}
                <div className="flex justify-center gap-2 mt-4">
                  {appScreenshots.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-2.5 h-2.5 rounded-full transition-colors ${
                        index === currentSlide ? 'bg-white' : 'bg-white/40 hover:bg-white/60'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-black text-gray-900 mb-6">
            {t('technology.experienceTechnology')}
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            {t('technology.experienceTechnologyDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/prices">
              <Button 
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg px-10 py-6"
              >
                {t('technology.viewProgramsPrices')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/signup">
              <Button 
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white font-bold text-lg px-10 py-6"
              >
                {t('technology.signUpNow')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TechnologyPage;
